<template>
    <div :class="`vanguard-contact-form ${isAspspEligible ? 'vanguard-aspsp-eligible' : 'vanguard-aspsp-no-eligible'}`">
      <div class="alert alert-primary" v-if="!isFormLoading && showPalyanceBanner">
        <p class="text-center m-0">
          {{ $t('form.dataLinkedTo') }} <strong>{{ aspspEmail }}</strong> {{ $t('form.haveBeenUsedToFillTheForm') }}
          {{ $t('form.youWillBeDeliveredAt') }} <strong>{{ aspspFullAddress }}</strong>
        </p>
        <div class="text-right">
          <small><a href="#" @click.prevent="editAspspPrefillData" class="text-muted">{{ $t('form.editMyAddress') }}</a></small>
        </div>
      </div>

      <div v-if="isFormLoading" class="vanguard-spinner-container">
        <div class="spinner-border" role="status">
          <span class="sr-only">{{ $t("all.loading") }}</span>
        </div>
      </div>

      <div v-show="!isFormLoading && !showPalyanceBanner" class="vanguard-contact-information">
          <div class="vanguard-billing-form">
            <!-- BILLING FORM -->
            <div class="col-sm-12">
              <div class="row form-group">
                <label class="vanguard-contact-form-label" for="billingEmail">{{ $t('form.email') }} <span class="vanguard-required-star">*</span></label>
                <input type="email"
                      :class="{'form-control': true, 'vanguard-contact-form-field': true, error: errors.has('billingEmail')}"
                      id="billingEmail" v-model="billingEmail"
                      data-vv-name="billingEmail" ref="billingEmail"
                      v-validate="'required|email|min:6'"
                      :disabled="isAspspEligible"
                      @input="userInput('billingEmail')">
                <!-- This is not my email -->
                <div class="text-right w-100" v-if="isAspspEligible">
                  <small><a href="#" class="text-muted" @click.prevent="removeWalletIdFromURLAndRedirect">{{ $t('form.thisIsNotMyEmail') }}</a></small>
                </div>
                <div v-if="errors.has('billingEmail')" class="vanguard-form-error">
                  {{ errors.first('billingEmail') }}
                </div>
              </div>
              <div class="row form-group">
                <label class="vanguard-contact-form-label" for="billingEmailConfirm">{{ $t('form.emailConfirm')}} <span class="vanguard-required-star">*</span></label>
                <input type="email"
                      :class="{'form-control': true, 'vanguard-contact-form-field': true, error: errors.has('billingEmailConfirm')}"
                      id="billingEmailConfirm" v-model="billingEmailConfirm"
                      data-vv-name="billingEmailConfirm" data-vv-as="billingEmail"
                      v-validate="'required|confirmed:billingEmail'"
                      :disabled="isAspspEligible"
                      @input="userInput('billingEmailConfirm')">
                <div v-if="errors.has('billingEmailConfirm')" class="vanguard-form-error">
                  {{ errors.first('billingEmailConfirm') }}
                </div>
              </div>
              <div class="row form-group form-group-gender">
                <label class="vanguard-contact-form-label" for="billingGender">{{ $t('form.gender') }}</label>
                <div class="pt-1">
                  <template v-for="gender in genders">
                      <div class="form-check form-check-inline" :key="'billing-gender-' + gender.code">
                          <input class="form-check-input" type="radio" name="billing-gender" :id="'billing-gender-' + gender.code" v-model="billingGender" :value="gender.code">
                          <label class="form-check-label" :for="'billing-gender-' + gender.code">{{ gender.label }}</label>
                      </div>
                  </template>
                </div>
                <div v-if="errors.has('billingGender')" class="vanguard-form-error">
                  {{ errors.first('billingGender') }}
                </div>
              </div>
              <div class="row form-group">
                <label class="vanguard-contact-form-label" for="billingFirstName">{{ $t('form.firstName') }} <span class="vanguard-required-star">*</span></label>
                <input type="text"
                  :class="{'form-control': true, 'vanguard-contact-form-field': true, error: errors.has('billingFirstName')}"
                  id="billingFirstName" v-model="billingFirstName"
                  data-vv-name="billingFirstName" v-validate="'required|min:2|max:40'"
                  @input="userInput('billingFirstName')">
                <div v-if="errors.has('billingFirstName')" class="vanguard-form-error">
                  {{ errors.first('billingFirstName') }}
                </div>
              </div>
              <div class="row form-group">
                <label class="vanguard-contact-form-label" for="billingLastName">{{ $t('form.lastName') }} <span class="vanguard-required-star">*</span></label>
                <input type="text"
                      :class="{'form-control': true, 'vanguard-contact-form-field': true, error: errors.has('billingLastName')}"
                      id="billingLastName" v-model="billingLastName"
                      data-vv-name="billingLastName" v-validate="'required|min:2|max:80'"
                      @input="userInput('billingLastName')">
                <div v-if="errors.has('billingLastName')" class="vanguard-form-error">
                  {{ errors.first('billingLastName') }}
                </div>
              </div>
              <div class="row form-group">
                <label class="vanguard-contact-form-label" for="billingPhoneNumber">{{ $t('form.phone')}}&nbsp;&nbsp;</label>
                <input type="text"
                      :class="{'form-control': true, 'vanguard-contact-form-field': true, error: errors.has('billingPhoneNumber')}"
                      id="billingPhoneNumber" v-model="billingPhoneNumber"
                      data-vv-name="billingPhoneNumber" v-validate="{max:20, regex: /^[+]*[(]{0,1}[0-9]{1,4}[)]{0,1}[-\s\./0-9]*$/i}"
                      @input="userInput('billingPhoneNumber')">
                <div v-if="errors.has('billingPhoneNumber')" class="vanguard-form-error">
                  {{ errors.first('billingPhoneNumber') }}
                </div>
              </div>
              <div class="row form-group">
                <label class="vanguard-contact-form-label" for="billingStreet3">{{ $t('form.normalizedStreet3') }} <span class="vanguard-required-star">*</span></label>
                <input type="text"
                      :class="{'form-control': true, 'vanguard-contact-form-field': true, error: errors.has('billingStreet3')}"
                      id="billingStreet3" v-model="billingStreet3"
                      data-vv-name="billingStreet3" v-validate="'required|min:6|max:96'"
                      @input="updateBillingStreet3()">
                <div v-if="errors.has('billingStreet3')" class="vanguard-form-error">
                  {{ errors.first('billingStreet3') }}
                </div>
              </div>
              <div class="row form-group">
                <label class="vanguard-contact-form-label" for="billingStreet1">{{ $t('form.normalizedStreet1') }}</label>
                <input type="text"
                      :class="{'form-control': true, 'vanguard-contact-form-field': true, error: errors.has('billingStreet1')}"
                      id="billingStreet1" v-model="billingStreet1"
                      data-vv-name="billingStreet1" v-validate="'max:96'"
                      @input="userInput('billingStreet1')">
                <div v-if="errors.has('billingStreet1')" class="vanguard-form-error">
                  {{ errors.first('billingStreet1') }}
                </div>
              </div>
              <div class="row form-group">
                <label class="vanguard-contact-form-label" for="billingStreet2">{{ $t('form.normalizedStreet2') }}</label>
                <input type="text"
                      :class="{'form-control': true, 'vanguard-contact-form-field': true, error: errors.has('billingStreet2')}"
                      id="billingStreet2" v-model="billingStreet2"
                      data-vv-name="billingStreet2" v-validate="'max:96'"
                      @input="userInput('billingStreet2')">
                <div v-if="errors.has('billingStreet2')" class="vanguard-form-error">
                  {{ errors.first('billingStreet2') }}
                </div>
              </div>
              <div class="row form-group">
                <label class="vanguard-contact-form-label" for="billingStreet4">{{ $t('form.normalizedStreet4') }}</label>
                <input type="text"
                      :class="{'form-control': true, 'vanguard-contact-form-field': true, error: errors.has('billingStreet4')}"
                      id="billingStreet4" v-model="billingStreet4"
                      data-vv-name="billingStreet4" v-validate="'max:96'"
                      @input="updateBillingStreet4()">
                <div v-if="errors.has('billingStreet4')" class="vanguard-form-error">
                  {{ errors.first('billingStreet4') }}
                </div>
              </div>
              <div class="row form-group">
                <label class="vanguard-contact-form-label" for="billingZipCode">{{ $t('form.zipCode') }} <span class="vanguard-required-star">*</span></label>
                <input type="text"
                      :class="{'form-control': true, 'vanguard-contact-form-field': true, error: errors.has('billingZipCode')}"
                      id="billingZipCode" v-model="billingZipCode"
                      data-vv-name="billingZipCode" v-validate="'required|min:3|max:10'"
                      @input="userInput('billingZipCode')">
                <div v-if="errors.has('billingZipCode')" class="vanguard-form-error">
                  {{ errors.first('billingZipCode') }}
                </div>
              </div>
              <div class="row form-group">
                <label class="vanguard-contact-form-label" for="billingCity">{{ $t('form.city') }} <span class="vanguard-required-star">*</span></label>
                <input type="text"
                      :class="{'form-control': true, 'vanguard-contact-form-field': true, error: errors.has('billingCity')}"
                      id="billingCity" v-model="billingCity"
                      data-vv-name="billingCity" v-validate="'required|max:40'"
                      @input="userInput('billingCity')">
                <div v-if="errors.has('billingCity')" class="vanguard-form-error">
                  {{ errors.first('billingCity') }}
                </div>
              </div>
              <div class="row form-group">
                <label class="vanguard-contact-form-label" for="billingCountry">{{ $t('form.country') }} <span class="vanguard-required-star">*</span></label>
                <select class="form-control vanguard-contact-form-field"
                        :class="{'form-control': true, 'vanguard-contact-form-field': true, error: errors.has('billingCountry')}"
                        id="billingCountry" v-model="billingCountry"
                        data-vv-name="billingCountry" v-validate="'required|max:80'"
                        @change="userInput('billingCountry')">
                  <option v-for="country in countries" :key="'billing-' + country.code" :value="country.code">
                    {{ country.label }}
                  </option>
                </select>
                <div v-if="errors.has('billingCountry')" class="vanguard-form-error">
                  {{ errors.first('billingCountry') }}
                </div>
              </div>
            </div>
          </div>

          <!-- CGV OPTIN -->
          <div class="vanguard-optins-container">
            <div class="row form-group form-check vanguard-cgu">
              <label class="form-check-label">
                <input type="checkbox" class="form-check-input" id="conditionsApproved" v-model="localConditionsApproved" data-vv-name="localConditionsApproved" v-validate="'required'">
                {{ $t('form.iAgree') }}
                <a :href="conditionsUrl" target="_blank">
                  {{ $t('form.conditions', {merchant: merchantName}) }}
                </a>
                <span class="vanguard-required-star">*</span>
              </label>
              <span></span>
            </div>
            <div v-if="errors.has('localConditionsApproved')" class="row vanguard-cgu vanguard-form-error" style="text-align:center;">
              {{ errors.first('localConditionsApproved') }}
            </div>
          </div>

          <!-- CGS OPTIN -->
          <div class="vanguard-optins-container">
            <div class="row form-group form-check vanguard-cgs">
              <label class="form-check-label">
                <input type="checkbox" class="form-check-input" id="cgsApproved" v-model="localCgsApproved" data-vv-name="localCgsApproved" v-validate="'required'">
                {{ $t('form.iAgree') }}
                <a :href="cgsUrl" target="_blank">{{ $t('form.serviceConditions') }}</a>
                <span class="vanguard-required-star">*</span>
              </label>
              <span></span>
            </div>
            <div v-if="errors.has('localCgsApproved')" class="row vanguard-cgs vanguard-form-error" style="text-align:center;">
              {{ errors.first('localCgsApproved') }}
            </div>
          </div>
      </div>
    </div>
</template>
<script>
import {mapGetters} from 'vuex';
import $ from 'jquery';

import VanguardConfig from '../../../config/vanguard';

import Utils from '../../common/modules/utils';

import * as mutations from '../store/mutations';
import * as constants from '../../common/constants';
import SentryLogger from '@/common/mixins/sentry-logger';

import MixinContactForm from '../../common/mixins/contact-form';
import MixinAspsp from '../../common/mixins/aspsp';
import configService from "@/common/services/configs";

export default {
  mixins: [
    MixinAspsp,
    SentryLogger,
    MixinContactForm
  ],
  data() {
    return {
      constants,
      isFormLoading: false,
      debounceHandle: null,
      filteredCountries: [],

      // Billing form
      billingEmail: '',
      billingEmailConfirm: '',
      billingGender: null,
      billingFirstName: '',
      billingLastName: '',
      billingPhoneNumber: '',
      billingStreet1: '',
      billingStreet2: '',
      billingStreet3: '',
      billingStreet4: '',
      billingZipCode: '',
      billingCity: '',
      billingCountry: 'FRA',

      localConditionsApproved: false,
      localCgsApproved: false,
      useSameAddressForShipping: true,

      // Shipping form
      shippingGender: null,
      shippingFirstName: '',
      shippingLastName: '',
      shippingPhoneNumber: '',
      shippingStreet1: '',
      shippingStreet2: '',
      shippingStreet3: '',
      shippingStreet4: '',
      shippingZipCode: '',
      shippingCity: '',
      shippingCountry: 'FRA'
    };
  },
  computed: {
    ...mapGetters([
      'locale',
      'globalConfig',
      'affiliateConfig',
      'journeyConfig',
      'conditionsApproved',
      'cgsApproved',
      'aspspEmail',
      'aspspFullAddress',
      'isAspspPrefillEditedByUser',
      'isAspspEligible',
      'showPalyanceBanner',
      'sfId',
      'prefillData',
      'sentry',
      'trackingCode',
      'paymentMethod',
      'choiceList'
    ]),
    Utils() {
      return Utils;
    },
    merchantName() {
      if (this.affiliateConfig) {
        return this.affiliateConfig.merchantName;
      }

      return '';
    },
    conditionsUrl() {
      if (this.affiliateConfig) {
        return this.affiliateConfig.conditionsUrl;
      }
    },
    cgsUrl() {
      if (this.globalConfig) {
        return this.globalConfig.palyanceCgs[this.locale];
      }
    },
    genders() {
      if (!this.globalConfig || !this.globalConfig.genders) {
        return [{Madame: {fr: 'Mme.'}, Monsieur: {fr: 'M.'}}];
      }

      const response = [];

      Object.keys(this.globalConfig.genders).forEach(sfApiKey => {
        response.push({
          code: sfApiKey,
          label: this.globalConfig.genders[sfApiKey][this.locale]
        });
      });

      return response;
    },
    countries() {
      if (!this.globalConfig || !this.globalConfig.countries) {
        return [{FRA: 'France'}]; // Fallback
      }

      const res = [];
      Object.keys(this.globalConfig.countries).forEach(codeIso3 => {
        if (typeof this.globalConfig.countries[codeIso3] !== 'undefined' && !this.filteredCountries.includes(codeIso3)) {
          // Secure the following usage (in case of the locale doesn't supporrted we use the default locale of Vanguard)
          let defaultLocale = this.locale;
          if (typeof this.globalConfig.countries[codeIso3] === 'undefined' || typeof this.globalConfig.countries[codeIso3][defaultLocale] === 'undefined') {
            defaultLocale = VanguardConfig.locale;
          }

          res.push({
            code: codeIso3,
            label: this.globalConfig.countries[codeIso3][defaultLocale]
          });
        }
      });
      // Sort lexicographically
      res.sort((a, b) => {
        return a.label.localeCompare(b.label);
      });
      return res;
    },
    currentChoiceShippingAmount() {
      if (!this.currentChoiceConfig.shippingAmount) {
        return 0;
      }

      const currentCountry = this.useSameAddressForShipping ? this.billingCountry : this.shippingCountry;
      if (!currentCountry) {
        return 0;
      }

      const shippingAmount = this.currentChoiceConfig.shippingAmount[currentCountry] || 0;
      return shippingAmount + ' ' + VanguardConfig.currencyIsoCode;
    }
  },
  methods: {
    /**
     * First, validate current field.
     * If Terms and Conditions are approved, then validate all fields.
     * Debounce timeout = 500ms.
     */
    userInput(fieldName) {
      if (this.debounceHandle) {
        window.clearTimeout(this.debounceHandle);
      }

      this.debounceHandle = window.setTimeout(() => {
        if (this.localConditionsApproved && this.localCgsApproved) {
          this.validateForm();
        } else if (fieldName) {
          this.validateField(fieldName);
        }
      }, 500);
    },
    updateBillingStreet3() {
      this.userInput('billingStreet3');

      this.emptyBillingStreet1();
      this.emptyBillingStreet2();
    },
    updateBillingStreet4() {
      this.userInput('billingStreet4');

      this.emptyBillingStreet1();
      this.emptyBillingStreet2();
    },
    emptyBillingStreet1() {
      this.billingStreet1 = '';
    },
    emptyBillingStreet2() {
      this.billingStreet2 = '';
    },
    afterPrefillForm() {
      /**
       * Validate all required field to know if we need to show the contact form fields
       */
      Promise.all([
        this.$validator.validate('billingEmail', undefined, {silent: true}),
        this.$validator.validate('billingEmailConfirm', undefined, {silent: true}),
        this.$validator.validate('billingFirstName', undefined, {silent: true}),
        this.$validator.validate('billingLastName', undefined, {silent: true}),
        this.$validator.validate('billingStreet3', undefined, {silent: true}),
        this.$validator.validate('billingZipCode', undefined, {silent: true}),
        this.$validator.validate('billingCity', undefined, {silent: true}),
        this.$validator.validate('billingCountry', undefined, {silent: true})
      ]).then(results => {
        const invalidField = results.find(result => result === false);

        if (typeof invalidField !== 'undefined') {
          console.warn('Form is not valid, showing the contact form field', invalidField, results);
          this.$store.commit(mutations.SET_IS_ASPSP_PREFILL_EDITED_BY_USER, true);
        }
      });

      this.validateForm();
    },
    /**
     * If billingEmailConfirm and current field are valid,
     * then store form data and send order draft.
     */
    validateField(fieldName) {
      this.$validator.validate('billingEmailConfirm', undefined, {silent: true}).then(valid => {
        if (!valid) {
          return;
        }

        this.$validator.validate(fieldName, undefined, {silent: true}).then(valid => {
          if (!valid) {
            return;
          }

          this.storeFormData(this);
        }).catch(error => {
          this.$log.error('Unable to validate field', fieldName, error);
        });
      }).catch(error => {
        this.$log.error('Unable to validate billingEmailConfirm', fieldName, error);
      });
    },
    /**
     * Validate the entiere form. If it is valid, store form data.
     */
    validateForm() {
      this.$validator.validateAll().then(valid => {
        if (this.choiceList.length <= 0) {
          this.$store.commit(mutations.SET_FORM_IS_VALID, false);
          return;
        }

        if (valid) {
          this.storeFormData(this);
        }
        this.$store.commit(mutations.SET_FORM_IS_VALID, valid);
      }).catch(error => {
        this.$log.error('Unable to validate form', error);
      });
    },
    /**
     * Remove hidden <option> elements to prevent Safari from displaying them in its overlay.
     */
    hideFilteredCountries() {
      const filtered = [];
      $('option').each((index, node) => {
        if ($(node).css('display') !== 'none') {
          return;
        }

        const val = $(node).val();
        if (val) {
          filtered.push(val);
        }
      });
      this.filteredCountries = filtered;
    },
    syncLocaleDataWithStore() {
      this.billingCountry = this.$store.getters.billingCountry;
      this.shippingCountry = this.$store.getters.shippingCountry;
    }
  },
  async mounted() {
    this.syncLocaleDataWithStore();

    // Create and dispatch the event
    // Becareful this order (set ESP and trigger this event) is important, in case of external manipulation, this event have to be trigger after our modifications
    const event = new CustomEvent('vanguard-contact-form-mounted', {});
    document.dispatchEvent(event);

    if (!this.affiliateConfig) {
      await Promise.all([
        configService.getAffiliateConfigFile.bind(this)(mutations),
        configService.getJourneyConfigFile.bind(this)(mutations),
        configService.getLocalConfigFile.bind(this)(mutations)
      ])
    }

    if (this.affiliateConfig) {
      this.getPrefillDataWithSfId(this, () => this.validateForm());

      this.getEligibilityData(this, () => {
        this.afterPrefillForm();
      });
    }

    // Apply the value of the store into the local variables (in case of testing)
    this.localConditionsApproved = this.conditionsApproved;
    this.localCgsApproved = this.cgsApproved;

    setTimeout(() => {
      this.hideFilteredCountries();
    }, 1000);
  },
  watch: {
    localConditionsApproved(value) {
      this.$store.commit(mutations.SET_CONDITIONS_APPROVED, value);
      this.userInput();
    },
    localCgsApproved(value) {
      this.$store.commit(mutations.SET_CGS_APPROVED, value);
      this.userInput();
    },
    useSameAddressForShipping(value) {
      this.$store.commit(mutations.SET_USE_SAME_ADDRESS_FOR_SHIPPING, value);
    },
    billingCountry(value) {
      this.$store.commit(mutations.SET_BILLING_COUNTRY, value);
    },
    shippingCountry(value) {
      this.$store.commit(mutations.SET_SHIPPING_COUNTRY, value);
    },
    choiceList() {
      if (this.localConditionsApproved && this.localCgsApproved) {
        this.validateForm();
      }
    }
  }
};
</script>



// WEBPACK FOOTER //
// contact-form.vue?6d62add6